import React, { useState } from "react";
import { Link, Outlet, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { motion } from "framer-motion";
import SiteFooter from "../components/SiteFooter";
import SiftWordmark from "../components/SiftWordmark";
import { useBranding } from "../contexts/BrandingContext";

export default function AuthLayout() {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const branding = useBranding();
  const isCreamLabel = branding.branded;

  const allNavLinks = [
    { to: "/", label: "Home", hideOnCreamLabel: true },
    { to: "/browse", label: "Browse", hideOnCreamLabel: true },
    { to: "/about", label: "About", hideOnCreamLabel: true },
    { to: "/contact", label: "Contact", hideOnCreamLabel: true },
    { to: "/auth/register", label: "Sign Up" },
    { to: "/get-started?a=business&c=1", label: "Company Signup", hideOnCreamLabel: true },
    { to: "/auth/login", label: "Sign in" },
  ];

  const navLinks = isCreamLabel
    ? allNavLinks.filter((item) => !item.hideOnCreamLabel)
    : allNavLinks;

  const isActive = (path) => location.pathname === path;

  const handleLinkClick = () => {
    setIsOpen(false);
  };

  // Don't render until branding is loaded (prevents flash of unbranded nav)
  const onCream = isCreamLabel; // cream-label canvases use the partner accent
  const pageBg = isCreamLabel ? branding.accent_color : "var(--bg)";
  const navBg = isCreamLabel ? branding.accent_color : "rgba(250,248,242,0.92)";

  if (!branding.loaded) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: "var(--bg)" }}>
        <div className="w-8 h-8 border-2 rounded-full animate-spin" style={{ borderColor: "var(--brand)", borderTopColor: "transparent" }} />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: pageBg, color: onCream ? "#fff" : "var(--ink)" }}>
      {/* Navigation Bar */}
      <nav
        className="sticky top-0 z-50 pt-[env(safe-area-inset-top)]"
        style={{
          backgroundColor: navBg,
          backdropFilter: onCream ? undefined : "saturate(180%) blur(12px)",
          WebkitBackdropFilter: onCream ? undefined : "saturate(180%) blur(12px)",
          borderBottom: onCream ? "1px solid rgba(255,255,255,0.18)" : "1px solid var(--border)",
        }}
      >
        <div className="max-w-[1480px] mx-auto px-4 sm:px-6 lg:px-10">
          <div className="flex justify-between items-center gap-6 min-h-16 md:min-h-20">
            {/* Logo */}
            <Link to={isCreamLabel ? "/auth/login" : "/"} className="select-none flex items-center hover:opacity-80 transition-opacity" onClick={handleLinkClick}>
              {isCreamLabel && branding.brand_logo_url ? (
                <img src={branding.brand_logo_url} alt={branding.brand_name} className="h-8 max-w-[200px] object-contain" />
              ) : isCreamLabel ? (
                <span className="text-white text-xl font-bold">{branding.brand_name}</span>
              ) : (
                <SiftWordmark size={30} />
              )}
            </Link>

            {/* Desktop Navigation */}
            <ul className="hidden lg:flex flex-wrap justify-end items-center gap-x-7 gap-y-2 min-w-0 text-sm">
              {navLinks.map((item) => (
                <li key={item.to} className="relative group">
                  <Link
                    to={item.to}
                    className="transition-colors"
                    style={{
                      color: onCream
                        ? (isActive(item.to) ? "#fff" : "rgba(255,255,255,.8)")
                        : (isActive(item.to) ? "var(--ink)" : "var(--text-secondary)"),
                      fontWeight: isActive(item.to) ? 600 : 500,
                    }}
                    onMouseEnter={(e) => { if (!onCream) e.currentTarget.style.color = "var(--ink)"; }}
                    onMouseLeave={(e) => { if (!onCream) e.currentTarget.style.color = isActive(item.to) ? "var(--ink)" : "var(--text-secondary)"; }}
                  >
                    {item.label}
                    <span
                      className="absolute -bottom-1 left-0 w-0 h-[2px] transition-all duration-300 group-hover:w-full"
                      style={{ backgroundColor: isCreamLabel ? branding.primary_color : "var(--brand)" }}
                    ></span>
                  </Link>
                </li>
              ))}
            </ul>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="lg:hidden p-2 rounded-lg transition-colors"
              style={{ color: onCream ? "#fff" : "var(--ink)" }}
              aria-label="Toggle menu"
            >
              {isOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation Menu */}
        {isOpen && (
          <div className="lg:hidden" style={{ backgroundColor: onCream ? navBg : "var(--surface)", borderTop: onCream ? "1px solid rgba(255,255,255,0.18)" : "1px solid var(--border)" }}>
            <ul className="px-4 py-4 space-y-1">
              {navLinks.map((item) => (
                <li key={item.to}>
                  <Link
                    to={item.to}
                    onClick={handleLinkClick}
                    className="block px-4 py-3 rounded-lg text-base font-medium transition-colors active:scale-95"
                    style={
                      isActive(item.to)
                        ? { background: onCream ? "rgba(255,255,255,.12)" : "var(--brand-tint)", color: onCream ? "#fff" : "var(--brand)" }
                        : { color: onCream ? "rgba(255,255,255,.85)" : "var(--text-secondary)" }
                    }
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        )}
      </nav>

      {/* Main Content Area with light card */}
      {/* The bottom inset matters as much as the top one here: "Don't have an
          account? Create account" is the last thing on this screen, and on
          Android it was rendering underneath the gesture bar. */}
      <main className="flex-1 flex items-center justify-center px-4 pt-8 sm:pt-10 lg:pt-12 pb-[calc(2rem+env(safe-area-inset-bottom))]">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="w-full max-w-xl sm:max-w-2xl lg:max-w-3xl"
        >
          <div className="rounded-3xl overflow-hidden" style={{ background: "var(--surface)", color: "var(--ink)", border: "1px solid var(--border)", boxShadow: "var(--shadow-xl, 0 30px 60px -30px rgba(27,24,19,.25))" }}>
            <div className="p-8 sm:p-10 lg:p-12">
              <Outlet />
            </div>
          </div>
        </motion.div>
      </main>

      {isCreamLabel ? (
        <footer className="py-6 text-center text-xs" style={{ color: "rgba(255,255,255,.55)", borderTop: "1px solid rgba(255,255,255,.1)" }}>
          Powered by{" "}
          <a href="https://siftcareers.com" target="_blank" rel="noopener noreferrer" className="underline" style={{ color: "rgba(255,255,255,.75)" }}>
            Sift Careers
          </a>
        </footer>
      ) : (
        <SiteFooter />
      )}
    </div>
  );
}
